/**
 * geminiRateLimiter.js
 * Tracks and enforces the 2-generations-per-5-minutes limit per logged-in account on the frontend.
 */

export const MAX_GENERATIONS = 2;
export const WINDOW_MS = 5 * 60 * 1000; // 5 minutes

export function getCurrentUserKey() {
  try {
    return localStorage.getItem('bursaku_current_user') || 'default_user';
  } catch {
    return 'default_user';
  }
}

function getStorageKey(itemId, userKey) {
  const user = userKey || getCurrentUserKey();
  const itemPart = itemId ? `_item_${itemId}` : '';
  return `bursaku_gemini_rate_limit_${user}${itemPart}`;
}

/**
 * Returns filtered array of valid generation timestamps within the active 5-minute sliding window.
 * @param {number|string} [itemId]
 * @param {string} [userKey]
 * @returns {Array<number>}
 */
export function getGenerationHistory(itemId, userKey) {
  const storageKey = getStorageKey(itemId, userKey);
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];

    const now = Date.now();
    // Keep only timestamps within WINDOW_MS
    const valid = list.filter((ts) => typeof ts === 'number' && now - ts < WINDOW_MS);

    if (valid.length !== list.length) {
      localStorage.setItem(storageKey, JSON.stringify(valid));
    }
    return valid.sort((a, b) => a - b);
  } catch {
    return [];
  }
}

/**
 * Checks rate limit status for a specific product and user account.
 * @param {number|string} [itemId]
 * @param {string} [userKey]
 * @returns {{ allowed: boolean, remaining: number, msRemaining: number, totalLimit: number }}
 */
export function checkRateLimitStatus(itemId, userKey) {
  const history = getGenerationHistory(itemId, userKey);
  const remaining = Math.max(0, MAX_GENERATIONS - history.length);
  const now = Date.now();

  let msRemaining = 0;
  if (remaining === 0 && history.length > 0) {
    // Oldest timestamp in the active window determines when next quota slot opens
    const oldest = history[0];
    msRemaining = Math.max(0, oldest + WINDOW_MS - now);
  }

  return {
    allowed: remaining > 0,
    remaining,
    msRemaining,
    totalLimit: MAX_GENERATIONS,
  };
}

/**
 * Returns whether a generation can be triggered for the specified product and user account.
 * @param {number|string} [itemId]
 * @param {string} [userKey]
 * @returns {boolean}
 */
export function canGenerate(itemId, userKey) {
  return checkRateLimitStatus(itemId, userKey).allowed;
}

/**
 * Records a successful generation event for a specific product and user account.
 * @param {number|string} [itemId]
 * @param {string} [userKey]
 */
export function recordGeneration(itemId, userKey) {
  const storageKey = getStorageKey(itemId, userKey);
  const history = getGenerationHistory(itemId, userKey);
  history.push(Date.now());
  try {
    localStorage.setItem(storageKey, JSON.stringify(history));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('bursaku-gemini-rate-limit-updated', { detail: { itemId } })
      );
    }
  } catch (err) {
    console.warn('Gagal menyimpan riwayat rate limit Gemini:', err);
  }
}

/**
 * Formats milliseconds into a clean human-readable countdown string (e.g. "4m 15s" or "32s").
 * @param {number} ms
 * @returns {string}
 */
export function formatCountdown(ms) {
  if (!ms || ms <= 0) return '0s';
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  }
  return `${seconds}s`;
}

